"use client";

import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { CalendarIcon, RefreshCcw, X, ChevronsUpDown, Check } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { Calendar } from "@/components/ui/calendar";
import { FilterItem } from "@/interfaces";
import moment from "moment";

type FilterControlsProps = {
  filters: FilterItem[];
  containerClassName?: string;
  itemClassName?: string;
  showActions?: boolean;
  onReset?: () => void;
  onApply?: () => void;
  onClearFilter?: (index: number) => void; // New prop for individual filter clear
};

/**
 * Helper function to map grid value to Tailwind col-span class
 * Tailwind requires explicit class names for JIT compilation
 */
const getColSpanClass = (gridValue: number | undefined): string => {
  const grid = gridValue || 3;
  const colSpanMap: Record<number, string> = {
    1: "col-span-1",
    2: "col-span-2",
    3: "col-span-3",
    4: "col-span-4",
    5: "col-span-5",
    6: "col-span-6",
    7: "col-span-7",
    8: "col-span-8",
    9: "col-span-9",
    10: "col-span-10",
    11: "col-span-11",
    12: "col-span-12",
  };
  return colSpanMap[grid] || "col-span-3";
};

/**
 * Combobox filter component with search functionality
 */
const ComboboxFilter = ({
  filter,
  commonClass,
  onClearFilter,
}: {
  filter: FilterItem;
  commonClass: string;
  onClearFilter?: () => void;
}) => {
  const [open, setOpen] = useState(false);
  const options = filter.options || [];
  const selectedOption = options.find((opt) => opt.value === filter.value);
  const hasValue = filter.value && filter.value !== "";

  return (
    <div className={cn(commonClass, "relative")}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              "w-full justify-between font-normal",
              !filter.value && "text-muted-foreground",
              hasValue && "pr-8"
            )}
          >
            {selectedOption ? selectedOption.label : filter.title}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-[var(--radix-popover-trigger-width)] p-0" align="start">
          <Command>
            <CommandInput placeholder={`Search ${filter.title.toLowerCase()}...`} />
            <CommandList>
              <CommandEmpty>No results found.</CommandEmpty>
              <CommandGroup>
                {options.map((opt) => (
                  <CommandItem
                    key={opt.value}
                    value={opt.label}
                    onSelect={() => {
                      filter.onValueChange(opt.value === filter.value ? "" : opt.value);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        filter.value === opt.value ? "opacity-100" : "opacity-0"
                      )}
                    />
                    {opt.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {hasValue && onClearFilter && (
        <Button
          variant="ghost"
          size="icon"
          className="absolute right-8 top-0 h-full w-8 hover:bg-transparent z-10"
          onClick={(e) => {
            e.stopPropagation();
            onClearFilter();
          }}
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
};

/**
 * Reusable component to render filter controls (inputs, selects, date pickers, etc.)
 * Can be used both inline and in a sheet
 */
export const FilterControls = ({
  filters,
  containerClassName,
  itemClassName,
  showActions = false,
  onReset,
  onApply,
  onClearFilter,
}: FilterControlsProps) => {
  return (
    <div className={cn("space-y-4", containerClassName)}>
      <div className={cn("grid gap-6", itemClassName || "grid-cols-12")}>
        {filters.map((filter, idx) => {
          // When using single column (like in sheet), ignore col-span
          const isSingleColumn = itemClassName?.includes("grid-cols-1");
          const colSpan = isSingleColumn ? "" : getColSpanClass(filter.grid);
          const commonClass = `w-full ${colSpan}`;

          // Check if filter has a value to show clear button
          const hasValue =
            filter.type === "search" ? filter.value && filter.value.trim() !== "" :
              filter.type === "date" ? filter.value !== null && filter.value !== undefined :
                filter.type === "dateRange" ? filter.value?.from !== null && filter.value?.from !== undefined :
                  filter.type === "select" ? filter.value && filter.value !== "" :
                    filter.type === "combobox" ? filter.value && filter.value !== "" :
                      false;

          switch (filter.type) {
            case "search":
              return (
                <div key={idx} className={cn(commonClass, "relative")}>
                  <Input
                    className="w-full pr-8"
                    placeholder={filter.title}
                    value={filter.value || ""}
                    maxLength={filter.maxLength}
                    onChange={(e) => filter.onValueChange(e.target.value)}
                  />
                  {hasValue && onClearFilter && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full w-8 hover:bg-transparent"
                      onClick={() => onClearFilter(idx)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              );
            case "select":
              return (
                <div key={idx} className={cn(commonClass, "relative")}>
                  <Select value={filter.value || ""} onValueChange={(val) => filter.onValueChange(val)}>
                    <SelectTrigger className={cn("w-full", hasValue && "pr-8")}>
                      <SelectValue placeholder={filter.title} />
                    </SelectTrigger>
                    <SelectContent>
                      {filter.options?.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {hasValue && onClearFilter && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute right-8 top-0 h-full w-8 hover:bg-transparent z-10"
                      onClick={() => onClearFilter(idx)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              );
            case "date":
              return (
                <div key={idx} className={cn(commonClass, "relative")}>
                  <Popover modal={false}>
                    <PopoverTrigger asChild className="w-full">
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !filter.value && "text-muted-foreground",
                          hasValue && "pr-8"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {filter.value ? moment(filter.value).format("MMM DD, YYYY") : filter.title}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-auto p-0 z-[100]"
                      side="bottom"
                      align="start"
                      sideOffset={4}
                    >
                      <Calendar
                        mode="single"
                        selected={filter.value}
                        onSelect={(val) => filter.onValueChange(val)}
                        initialFocus
                        disabled={typeof filter.disabledDates === "function" ? filter.disabledDates : filter.disabledDates}
                      />
                    </PopoverContent>
                  </Popover>
                  {hasValue && onClearFilter && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full w-8 hover:bg-transparent z-10"
                      onClick={() => onClearFilter(idx)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              );
            case "dateRange":
              return (
                <div key={idx} className={commonClass}>
                  <Popover modal={false}>
                    <PopoverTrigger asChild className="w-full">
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !filter.value?.from && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {filter.value?.from ? (
                          filter.value.to ? (
                            <>
                              {moment(filter.value.from).format("MMM DD, YYYY")} – {moment(filter.value.to).format("MMM DD, YYYY")}
                            </>
                          ) : (
                            moment(filter.value.from).format("MMM DD, YYYY")
                          )
                        ) : (
                          filter.title
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-auto p-0 z-[100]"
                      side="bottom"
                      align="start"
                      sideOffset={4}
                    >
                      <Calendar
                        initialFocus
                        mode="range"
                        defaultMonth={filter.value?.from}
                        selected={filter.value}
                        onSelect={(range) => filter.onValueChange(range)}
                        numberOfMonths={2}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              );
            case "combobox":
              return (
                <ComboboxFilter
                  key={idx}
                  filter={filter}
                  commonClass={commonClass}
                  onClearFilter={onClearFilter ? () => onClearFilter(idx) : undefined}
                />
              );
            default:
              return null;
          }
        })}
        {showActions && filters.length > 0 && onReset && (
          <div className="w-full col-span-2">
            <Button variant="secondary" onClick={onReset} className="w-full uppercase">
              <RefreshCcw className="w-4 h-4 mr-2" />
              Reset
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

